import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, Observable } from 'rxjs';
import { View } from '../../../../shared/store/view/types';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SubHeaderService } from '../../../../shared/store/subheader/subheader.service';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ViewService } from '../../../../shared/store/view/views.service';
import { FormService } from '../../../../shared/store/form/form.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';
import {
  ProductCategoryDocumentType,
  ProductCategoryModel,
} from 'src/app/shared/store/product-category/types';
import { ProductCategoryService } from 'src/app/shared/store/product-category/product-category.service';
import {
  DataType,
  DropdownStyle,
  SourceEnum,
} from '../../../../shared/store/typeahead/types';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from '../../../../shared/service/modal.service';
import { DocumentTypeModel } from '../../../../shared/store/document-type/types';
import { ModalContentComponent } from '../../../../shared/components/modal-content/modal-content.component';
import { toFirstLetterLower } from '../../../../shared/helper/stringhelper';
import { ActionButton } from '../../../../shared/components/modal-content/action-button';
import { ModalComponentKey } from 'src/app/shared/service/modal-component';

@UntilDestroy()
@Component({
  selector: 'app-product-category-create',
  templateUrl: './product-category-create.component.html',
  styleUrls: ['./product-category-create.component.scss'],
  standalone: false,
})
export class ProductCategoryCreateComponent implements OnInit, OnDestroy {
  view$: Observable<View>;
  productCategoryForm: FormGroup;
  productCategoryCreateInstance$: Observable<ProductCategoryModel>;

  protected readonly DataType = DataType;
  protected readonly DropdownStyle = DropdownStyle;

  constructor(
    private readonly router: Router,
    private readonly productCategoryService: ProductCategoryService,
    private readonly subHeaderService: SubHeaderService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly viewService: ViewService,
    private readonly formService: FormService,
    private readonly formBuilder: FormBuilder,
    private readonly modalService: NgbModal,
    private readonly modalExchangeService: ModalService,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.productCategoryCreateInstance$ =
      this.productCategoryService.createInstance.value$;
  }

  ngOnInit(): void {
    this.subHeaderService
      .getTriggerAction()
      .pipe(untilDestroyed(this))
      .subscribe((x) => {
        if (x == 'productCategoryCreatePush') this.onSubmit();
        if (x == 'productCategoryList') {
          this.formService.clear();
          this.router.navigate(['Insurance/ProductCategories']);
        }
        this.subHeaderService.setTriggerAction('');
      });

    this.view$
      .pipe(
        untilDestroyed(this),
        filter((x) => !!x),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@list', x.title);
        this.breadcrumbService.set(
          '@productCategoryCreate',
          x.breadcrumbCreate,
        );
      });

    this.productCategoryForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: [''],
      productCategoryType: [1, [Validators.required]],
      productCategoryEnumKey: [],
      sequence: [99],
      parentId: [undefined],
      icon: [''],
      productCategoryDocumentTypes: this.formBuilder.array([]),
      defaultTaxRate: [0, [Validators.required]],
      code: ['', [Validators.required, Validators.maxLength(3)]],
    });

    this.productCategoryService.createInstance.call();
    this.productCategoryCreateInstance$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.productCategoryForm?.patchValue({
          name: x.name,
          description: x.description,
          productCategoryType: x.productCategoryType,
          productCategoryEnumKey: x.productCategoryEnumKey,
          sequence: x.sequence,
          icon: x.icon,
          defaultTaxRate: x.defaultTaxRate,
        });
        const existingProductCategoryDocumentTypes =
          x.productCategoryDocumentTypes;
        let patchProductCategoryDocumentTypes: any[] = [];
        if (existingProductCategoryDocumentTypes) {
          for (
            let i = 0;
            i < existingProductCategoryDocumentTypes.length;
            i++
          ) {
            this.addProductCategoryDocumentType();
            patchProductCategoryDocumentTypes.push({
              documentTypeId:
                existingProductCategoryDocumentTypes[i].documentTypeId,
              documentTypeName:
                existingProductCategoryDocumentTypes[i].documentTypeName,
              isMandatory: existingProductCategoryDocumentTypes[i].mandatory,
              documentFlow:
                existingProductCategoryDocumentTypes[i].documentFlow,
            });
          }
        }
        this.productCategoryDocumentTypes.patchValue(
          patchProductCategoryDocumentTypes,
        );
      });
  }

  onSubmit() {
    if (this.productCategoryForm.invalid) {
      this.productCategoryForm.markAllAsTouched();
      this.formService.setEntityErrors('PRODUCT_CATEGORY');
      this.formService.countErrors(this.productCategoryForm, true);
      return;
    }

    let productCategoryDocumentTypes: ProductCategoryDocumentType[] = [];

    this.productCategoryForm.value.productCategoryDocumentTypes.forEach((x) => {
      const productCategoryDocumentType: ProductCategoryDocumentType = {
        productCategoryId: 0,
        documentTypeId: x.documentTypeId,
        documentFlow: x.documentFlow,
        mandatory: x.isMandatory,

        //Others
        id: 0,
        documentTypeName: x.documentTypeName,
        documentTypeDescription: '',
        productCategoryName: '',
      };
      productCategoryDocumentTypes.push(productCategoryDocumentType);
    });

    let productCategoryModel: ProductCategoryModel = {
      id: 0,
      name: this.productCategoryForm.value.name!,
      description: this.productCategoryForm.value.description!,
      productCategoryType: this.productCategoryForm.value.productCategoryType!,
      productCategoryEnumKey:
        this.productCategoryForm.value.productCategoryEnumKey!,
      sequence: this.productCategoryForm.value.sequence!,
      icon: this.productCategoryForm.value.icon!,
      defaultTaxRate: this.productCategoryForm.value.defaultTaxRate!,
      code: this.productCategoryForm.value.code!,

      productCategoryDocumentTypes: productCategoryDocumentTypes,
      //Others
      insuranceCoverages: [],
      productCategoryTypeName: '',
      claimTypes: [],
    };

    let result$ = this.productCategoryService.add.call(productCategoryModel);

    result$
      .pipe(
        filter((x) => !!x),
        take(1),
      )
      .subscribe((result) => {
        this.formService.clear();
        this.router.navigate([
          '/Insurance/ProductCategories/Details',
          result.id,
        ]);
      });
  }

  ngOnDestroy(): void {
    this.productCategoryService.add.reset();
    this.breadcrumbService.set('@productCategoryCreate', ' ');
  }

  openModalChoiceDocumentType() {
    let documentTypes: DocumentTypeModel[] = [];

    this.productCategoryForm.value.productCategoryDocumentTypes.forEach((x) => {
      const documentType: DocumentTypeModel = {
        id: x.documentTypeId,
        name: x.documentTypeName,
        description: '',
      };
      documentTypes.push(documentType);
    });

    const modalRef = this.modalService.open(ModalContentComponent, {
      size: 'lg',
      backdrop: 'static',
    });

    modalRef.componentInstance.component =
      this.modalExchangeService.getComponentClass(
        ModalComponentKey.SelectDocumentTypeEdit,
      );
    modalRef.componentInstance.title = 'Sélection des documents';
    modalRef.componentInstance.cancelButton = true;
    modalRef.componentInstance.buttons = [
      <ActionButton>{
        actionName: toFirstLetterLower('documentType') + 'Update',
      },
    ];
    modalRef.componentInstance.inputParams = {
      source: SourceEnum.Modal,
      selectedDocumentTypes: documentTypes,
    };

    // The selection comes back as the modal result; a dismiss rejects the promise, hence the catch.
    modalRef.result
      .then((selection: DocumentTypeModel[]) =>
        this.applyProductCategoryDocumentTypes(selection),
      )
      .catch(() => {});
  }

  /**
   * Rebuilds the product category document rows from the modal selection, keeping the flow and
   * mandatory flag already entered on the rows that survive the selection.
   */
  private applyProductCategoryDocumentTypes(selection: DocumentTypeModel[]) {
    const values = this.productCategoryForm.value.productCategoryDocumentTypes;

    const rows = selection.map((documentType) => {
      const existing = values.find(
        (pcdt) => pcdt.documentTypeId === documentType.id,
      );

      return (
        existing ?? {
          documentTypeId: documentType.id,
          documentTypeName: documentType.name,
          isMandatory: true,
          documentFlow: 'ToReceive',
        }
      );
    });

    this.productCategoryDocumentTypes.clear();
    for (let i = 0; i < rows.length; i++) {
      this.addProductCategoryDocumentType();
    }
    this.productCategoryDocumentTypes.patchValue(rows);
  }

  addProductCategoryDocumentType() {
    const productCategoryDocumentTypeForm = this.formBuilder.group({
      documentTypeId: [0],
      documentTypeName: [''],
      isMandatory: [true],
      documentFlow: ['ToReceive'],
    });

    this.productCategoryDocumentTypes.push(productCategoryDocumentTypeForm);
  }

  removeProductCategoryDocumentType(index: number) {
    this.productCategoryDocumentTypes.removeAt(index);
  }

  get productCategoryDocumentTypes() {
    return this.productCategoryForm.controls[
      'productCategoryDocumentTypes'
    ] as FormArray;
  }

  onBlurName($event: FocusEvent) {
    let formControlValue = this.productCategoryForm.get('code');
    const name = this.productCategoryForm.get('name')?.value;
    formControlValue?.setValue(name.slice(0, 3).toUpperCase());
  }
}
